import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DemandRecord, DemandType } from '../../services/partDemandTypes';
import { DataTable } from '../DataTable';
import { formatDate } from '../quoteToCash/format';

import { customerLabel, formatQty } from './format';

type SortKey = 'date' | 'qty' | 'customerName' | 'tranType';

/** The transactions behind the figures, newest first, by demand type and sortable */
export const DemandTransactionsTable: React.FC<{
  transactions: DemandRecord[];
  uom: string;
}> = ({ transactions, uom }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [type, setType] = useState<DemandType | 'all'>('all');
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({
    key: 'date',
    desc: true,
  });

  const rows = useMemo(() => {
    const filtered = transactions.filter(
      (r) => type === 'all' || r.demandType === type
    );
    const sign = sort.desc ? -1 : 1;
    return [...filtered].sort((a, b) => {
      const x = a[sort.key];
      const y = b[sort.key];
      const order =
        typeof x === 'number'
          ? x - (y as number)
          : String(x).localeCompare(String(y));
      return sign * (order || a.tranNum - b.tranNum);
    });
  }, [transactions, type, sort]);
  const total = rows.reduce((sum, r) => sum + r.qty, 0);

  const onSort = (key: string) =>
    setSort((s) => ({
      key: key as SortKey,
      desc: s.key === key ? !s.desc : key === 'date' || key === 'qty',
    }));
  const arrow = (key: SortKey) =>
    sort.key === key ? (sort.desc ? ' ↓' : ' ↑') : '';

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div
          className="inline-flex rounded-md border border-gray-300 bg-white p-0.5"
          role="group"
          aria-label={t('partDemand.transactions.filter')}
        >
          {(['all', 'customer', 'internal', 'return'] as const).map(
            (option) => (
              <button
                key={option}
                type="button"
                aria-pressed={type === option}
                onClick={() => setType(option)}
                className={`px-3 py-1 text-sm font-medium rounded ${
                  type === option
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {t(`partDemand.demandType.${option}`)}
              </button>
            )
          )}
        </div>
        <p className="text-sm text-gray-600">
          {t('partDemand.transactions.total', {
            count: rows.length,
            qty: formatQty(total, uom, lang),
          })}
        </p>
      </div>
      <DataTable
        data={rows}
        keyExtractor={(row) => row.tranNum}
        isEmpty={rows.length === 0}
        emptyMessage={t('partDemand.transactions.none')}
        onColumnSort={onSort}
        columns={[
          {
            key: 'date',
            header: t('partDemand.transactions.date') + arrow('date'),
            sortable: true,
            render: (value) => formatDate(value as string, lang, 'long'),
          },
          {
            key: 'demandType',
            header: t('partDemand.transactions.type'),
            render: (value) => t(`partDemand.demandType.${value as string}`),
          },
          {
            key: 'qty',
            header: t('partDemand.transactions.qty') + arrow('qty'),
            sortable: true,
            align: 'right',
            render: (value) => formatQty(value as number, uom, lang),
          },
          {
            key: 'customerName',
            header:
              t('partDemand.transactions.customer') + arrow('customerName'),
            sortable: true,
            render: (_, row) => (row ? customerLabel(row) : ''),
          },
          {
            key: 'orderNum',
            header: t('partDemand.transactions.order'),
            render: (_, row) =>
              row?.orderNum
                ? `${row.orderNum}/${row.orderLine}/${row.orderRelNum}`
                : '',
          },
          {
            key: 'jobNum',
            header: t('partDemand.transactions.job'),
            render: (_, row) =>
              row?.jobNum
                ? `${row.jobNum}/${row.assemblySeq}/${row.jobSeq}`
                : '',
          },
          {
            key: 'reference',
            header: t('partDemand.transactions.reference'),
            render: (_, row) =>
              [
                row?.packNum
                  ? t('partDemand.transactions.packSlip', {
                      pack: row.packNum,
                      line: row.packLine,
                    })
                  : '',
                row?.rmaNum
                  ? t('partDemand.transactions.rma', { rma: row.rmaNum })
                  : '',
                row?.reference ?? '',
              ]
                .filter(Boolean)
                .join(' · '),
          },
          {
            key: 'tranType',
            header: t('partDemand.transactions.tranType') + arrow('tranType'),
            sortable: true,
          },
        ]}
      />
    </>
  );
};
